import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/cabinet.js';
import type {
  AccountStatus, CategorySpend, FinancialAccount, Holding, MoneyTransaction, NetWorth, InstrumentSpec,
} from '../lib/cabinet.js';
import { Instrument, SectionLabel } from '../components/instruments/index.js';
import './money.css';

/**
 * MONEY surface — the books.
 *
 * Two rules shape everything below, because getting either wrong produces a
 * number that is confidently wrong rather than obviously missing:
 *
 *  1. A transaction `amount` is POSITIVE when money LEFT the account. It is
 *     rendered as an outflow (−) and income as an inflow (+). Never inverted.
 *  2. Net worth computed from a subset of accounts is never shown as if it
 *     were complete. When `accounts_counted < accounts_total` the shortfall
 *     is on screen, next to the number, in the same eyeline — along with the
 *     oldest balance timestamp feeding it.
 *
 * The ledger is filled by hand: accounts are created and transactions imported
 * through Cabinet's own tools. Empty is therefore a completely normal state
 * for this surface, and it renders as one rather than as a failure.
 */

const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TXN_DAYS = 14;
const SPEND_DAYS = 30;

/* -------------------------------------------------------------- format --- */

/** `$1,234.56` — unsigned. Callers own the sign, because the sign is meaning. */
function usd(n: number, cents = true): string {
  const fixed = Math.abs(n).toFixed(cents ? 2 : 0);
  const [whole = '0', frac] = fixed.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `$${grouped}${frac ? `.${frac}` : ''}`;
}

/** Signed, for a figure that can legitimately go either way (net worth). */
function signedUsd(n: number, cents = true): string {
  return `${n < 0 ? '−' : ''}${usd(n, cents)}`;
}

/* The server's timestamps come from SQLite's datetime('now') — naive UTC with
   no zone marker, which `new Date()` would happily misread as local time and
   quietly shift by hours. Parsed literally instead, and labelled UTC so the
   reading is honest rather than plausible. */
const TS = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/;

function fmtStamp(s: string | null): string {
  if (!s) return 'never';
  const m = TS.exec(s);
  if (!m) return s;
  const [, , mo = '', d = '', hh = '', mi = ''] = m;
  return `${MONTHS[Number(mo)] ?? ''} ${Number(d)} · ${hh}:${mi} UTC`;
}

function fmtDate(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (!m) return day;
  const [, , mo = '', d = ''] = m;
  return `${MONTHS[Number(mo)] ?? ''} ${Number(d)}`;
}

/** `FOOD_AND_DRINK_RESTAURANT` → `Food and drink restaurant`. */
function humanize(raw: string | null): string {
  if (!raw) return 'Uncategorized';
  const words = raw.toLowerCase().split('_').filter(Boolean);
  const [first = '', ...rest] = words;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ');
}

function errText(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

/* -------------------------------------------------------------- pieces --- */

const STATUS_COPY: Record<AccountStatus, { label: string; tone: 'ok' | 'warn' | 'crit' | 'dim' }> = {
  active: { label: 'current', tone: 'ok' },
  login_required: { label: 'needs sign-in', tone: 'crit' },
  error: { label: 'error', tone: 'crit' },
  revoked: { label: 'closed', tone: 'dim' },
};

/**
 * Net worth, and the honesty that has to travel with it. `accounts_counted`
 * vs `accounts_total` is rendered whenever they disagree — an account with no
 * current balance silently vanishes from the sum, and the result looks
 * exactly like a real drop.
 */
function NetWorthHeader({ nw }: { nw: NetWorth }) {
  const short = nw.accounts_total - nw.accounts_counted;
  const partial = short > 0;

  const specs: InstrumentSpec[] = [
    {
      kind: 'stat',
      label: 'Net worth',
      big: signedUsd(nw.net_worth, false),
      unit: partial ? 'partial' : undefined,
      sub: `cash + investments − debts${partial ? ` · ${nw.accounts_counted} of ${nw.accounts_total} accounts` : ''}`,
      tone: nw.net_worth < 0 ? 'crit' : 'ok',
      ...(partial ? { tag: `${nw.accounts_counted}/${nw.accounts_total}`, tagTone: 'warn' as const } : {}),
    },
    { kind: 'stat', label: 'Cash', big: usd(nw.cash, false), sub: 'checking + savings' },
    { kind: 'stat', label: 'Investments', big: usd(nw.investments, false), sub: 'brokerage + retirement' },
    {
      kind: 'stat',
      label: 'Credit owed',
      big: usd(nw.credit, false),
      sub: nw.loans > 0 ? `plus ${usd(nw.loans, false)} in loans` : 'cards, balance outstanding',
      tone: nw.credit > 0 ? 'warn' : 'default',
    },
  ];

  return (
    <section className="money-networth" aria-label="Net worth">
      <SectionLabel n="01">Where you stand</SectionLabel>
      <div className="money-nw-grid">
        {specs.map((spec, i) => (
          <Instrument key={`nw-${i}`} spec={spec} />
        ))}
      </div>
      {partial && (
        <p className="money-caveat" role="status">
          <span className="money-caveat-mark" aria-hidden="true">
            △
          </span>
          Computed from {nw.accounts_counted} of {nw.accounts_total} accounts — {short}{' '}
          {short === 1 ? 'has' : 'have'} no current balance, so {short === 1 ? 'it is' : 'they are'} missing from
          this total. Treat it as a floor, not the figure.
        </p>
      )}
      <p className="money-asof data">
        Balances as of {fmtStamp(nw.stalest_balance_at)}
        {nw.stalest_balance_at ? ' (oldest contributing)' : ''}
      </p>
    </section>
  );
}

interface Group {
  label: string;
  /** The worst status among the group's accounts — what the heading reports. */
  status: AccountStatus;
  accounts: FinancialAccount[];
}

/**
 * Accounts hang off institutions, so the institution is the outer loop. The
 * join is by institution NAME because that is the only thing an account row
 * carries; accounts with no institution land together under one heading rather
 * than being dropped.
 */
function groupAccounts(accounts: FinancialAccount[]): Group[] {
  const groups: Group[] = [];
  const byName = new Map<string, Group>();
  for (const a of accounts) {
    const label = a.institution_name ?? 'Unfiled accounts';
    let g = byName.get(label);
    if (!g) {
      g = { label, status: a.item_status, accounts: [] };
      byName.set(label, g);
      groups.push(g);
    }
    // Anything other than `active` is the state worth reporting, and the first
    // such account decides the heading — one broken account in a group is not
    // made fine by three healthy ones beside it.
    if (g.status === 'active' && a.item_status !== 'active') g.status = a.item_status;
    g.accounts.push(a);
  }
  return groups;
}

function AccountRow({
  account,
  busy,
  onToggleHidden,
}: {
  account: FinancialAccount;
  busy: boolean;
  onToggleHidden: () => void;
}) {
  const hidden = account.hidden === 1;
  // Credit and loan balances arrive positive-as-owed. Showing them beside a
  // cash balance without saying so would read as money you have.
  const owed = account.type === 'credit' || account.type === 'loan';
  const bal = account.current_balance;

  return (
    <li className={`money-acct${hidden ? ' is-hidden' : ''}`}>
      <div className="money-acct-id">
        <span className="money-acct-name">{account.name ?? 'Unnamed account'}</span>
        <span className="money-acct-meta data">
          {account.mask ? `••${account.mask}` : 'no mask'} · {account.subtype ?? account.type ?? 'account'}
          {hidden ? ' · hidden from totals' : ''}
        </span>
      </div>
      <div className="money-acct-bal">
        {bal === null ? (
          <span className="money-acct-nobal data">no balance</span>
        ) : (
          <>
            <span className={`money-acct-num data${owed ? ' owed' : ''}`}>{usd(bal)}</span>
            {owed && <span className="money-acct-owed data">owed</span>}
            {!owed && account.available_balance !== null && account.available_balance !== bal && (
              <span className="money-acct-owed data">{usd(account.available_balance)} available</span>
            )}
          </>
        )}
      </div>
      <button type="button" className="money-btn subtle tiny" disabled={busy} onClick={onToggleHidden}>
        {busy ? '…' : hidden ? 'Show' : 'Hide'}
      </button>
    </li>
  );
}

function TransactionsTable({ transactions }: { transactions: MoneyTransaction[] }) {
  return (
    <section className="money-txns" aria-label="Recent transactions">
      <SectionLabel n="03">Recent activity</SectionLabel>
      <p className="money-lede data">Last {TXN_DAYS} days. Outflows are marked −, money in +.</p>
      {transactions.length === 0 ? (
        <p className="money-empty voice">Nothing has moved in the last {TXN_DAYS} days.</p>
      ) : (
        <table className="money-grid">
          <thead>
            <tr>
              <th scope="col">date</th>
              <th scope="col">merchant</th>
              <th scope="col">category</th>
              <th scope="col">account</th>
              <th scope="col" className="num">
                amount
              </th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((t) => {
              // POSITIVE amount = money spent. This is the one line in the
              // surface where an inverted sign would look entirely plausible.
              const outflow = t.amount >= 0;
              const pending = t.pending === 1;
              return (
                <tr key={t.transaction_id} className={pending ? 'is-pending' : undefined}>
                  <td className="data">{fmtDate(t.date)}</td>
                  <th scope="row">
                    <span className="money-merchant">{t.merchant ?? 'Unknown'}</span>
                    {pending && (
                      <span className="money-chip pending" title="Provisional — the amount and merchant can still change">
                        pending
                      </span>
                    )}
                  </th>
                  <td className="money-cat">{humanize(t.category_primary)}</td>
                  <td className="data money-acct-cell">
                    {t.account ?? '—'}
                    {t.mask ? ` ••${t.mask}` : ''}
                  </td>
                  <td className={`num data money-amt ${outflow ? 'out' : 'in'}`}>
                    {outflow ? '−' : '+'}
                    {usd(t.amount)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

function CategoryPanel({ categories }: { categories: CategorySpend[] }) {
  const total = categories.reduce((s, c) => s + c.spent, 0);
  const peak = categories.reduce((m, c) => Math.max(m, c.spent), 0);

  return (
    <section className="money-cats" aria-label="Spending by category">
      <SectionLabel n="04">Where it went</SectionLabel>
      <p className="money-lede data">
        Last {SPEND_DAYS} days · {usd(total)} out across {categories.length} categor
        {categories.length === 1 ? 'y' : 'ies'}
      </p>
      {categories.length === 0 ? (
        <p className="money-empty voice">No categorised spending in the window yet.</p>
      ) : (
        <ul className="money-cat-list">
          {categories.map((c) => (
            <li key={c.category} className="money-cat-row">
              <span className="money-cat-name">{humanize(c.category)}</span>
              <span className="money-cat-bar" aria-hidden="true">
                <span className="money-cat-fill" style={{ width: `${peak > 0 ? (c.spent / peak) * 100 : 0}%` }} />
              </span>
              <span className="money-cat-amt data">{usd(c.spent)}</span>
              <span className="money-cat-n data">
                {c.txns} txn{c.txns === 1 ? '' : 's'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function HoldingsPanel({ holdings }: { holdings: Holding[] }) {
  const total = holdings.reduce((s, h) => s + (h.value ?? 0), 0);
  return (
    <section className="money-holdings" aria-label="Holdings">
      <SectionLabel n="05">Holdings</SectionLabel>
      {holdings.length === 0 ? (
        <p className="money-empty voice">
          No investment accounts yet. Nothing&rsquo;s wrong — there&rsquo;s simply nothing to price.
        </p>
      ) : (
        <>
          <p className="money-lede data">{usd(total)} across {holdings.length} positions</p>
          <table className="money-grid">
            <thead>
              <tr>
                <th scope="col">ticker</th>
                <th scope="col">security</th>
                <th scope="col">account</th>
                <th scope="col" className="num">
                  qty
                </th>
                <th scope="col" className="num">
                  price
                </th>
                <th scope="col" className="num">
                  value
                </th>
              </tr>
            </thead>
            <tbody>
              {holdings.map((h, i) => (
                <tr key={`${h.ticker ?? h.security ?? 'holding'}-${i}`}>
                  <td className="data">{h.ticker ?? '—'}</td>
                  <th scope="row">{h.security ?? 'Unnamed security'}</th>
                  <td className="data money-acct-cell">{h.account ?? h.institution ?? '—'}</td>
                  <td className="num data">{h.quantity === null ? '—' : h.quantity.toFixed(3)}</td>
                  <td className="num data">{h.price === null ? '—' : usd(h.price)}</td>
                  <td className="num data">{h.value === null ? '—' : usd(h.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

/* --------------------------------------------------------------- surface --- */

export function Money() {
  const [accounts, setAccounts] = useState<FinancialAccount[] | null>(null);
  const [netWorth, setNetWorth] = useState<NetWorth | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<MoneyTransaction[]>([]);
  const [categories, setCategories] = useState<CategorySpend[]>([]);
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [tick, setTick] = useState(0);

  const [acctBusy, setAcctBusy] = useState<Record<number, boolean>>({});

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let live = true;
    api
      .moneyAccounts()
      .then((r) => {
        if (!live) return;
        setAccounts(r.accounts);
        setNetWorth(r.net_worth);
        setLoadError(null);
      })
      .catch((e: unknown) => {
        if (!live) return;
        setAccounts(null);
        setNetWorth(null);
        setLoadError(errText(e, "Couldn't open the books."));
      });
    return () => {
      live = false;
    };
  }, [tick]);

  // The ledger reads are independent of the accounts read on purpose: a
  // transactions query that fails should cost this surface its transactions
  // table, not its balances.
  useEffect(() => {
    let live = true;
    api
      .moneyTransactions({ days: TXN_DAYS, limit: 100 })
      .then((r) => live && setTransactions(r.transactions))
      .catch(() => live && setTransactions([]));
    api
      .moneyCategories(SPEND_DAYS)
      .then((r) => live && setCategories(r.categories))
      .catch(() => live && setCategories([]));
    api
      .moneyHoldings()
      .then((r) => live && setHoldings(r.holdings))
      .catch(() => live && setHoldings([]));
    return () => {
      live = false;
    };
  }, [tick]);

  const toggleHidden = useCallback((account: FinancialAccount) => {
    const next = account.hidden !== 1;
    setAcctBusy((b) => ({ ...b, [account.id]: true }));
    api
      .moneySetAccountHidden(account.id, next)
      // Hiding an account changes what net worth is computed from, so the
      // headline number has to be re-read — patching the row in place would
      // leave the total contradicting the list right above it.
      .then(reload)
      .catch(() => {
        /* the row stays as it was; a reload will show the truth */
      })
      .finally(() =>
        setAcctBusy((b) => {
          const { [account.id]: _drop, ...rest } = b;
          return rest;
        }),
      );
  }, [reload]);

  const groups = useMemo(() => (accounts ? groupAccounts(accounts) : []), [accounts]);

  if (!accounts || !netWorth) {
    return (
      <section className="money" aria-label="Money">
        <p className={loadError ? 'money-empty voice' : 'money-loading data'}>
          {loadError ?? 'Opening the books…'}
        </p>
      </section>
    );
  }

  return (
    <section className="money" aria-label="Money">
      <header className="money-head">
        <div>
          <SectionLabel n="00">The books</SectionLabel>
          <p className="money-lede voice">
            Every account, what it holds, and where the money went. Numbers here are only ever as complete as
            what has been entered behind them — so both are on the same page.
          </p>
        </div>
      </header>

      <NetWorthHeader nw={netWorth} />

      <section className="money-accounts" aria-label="Accounts">
        <SectionLabel n="02">Accounts</SectionLabel>
        {groups.length === 0 ? (
          <p className="money-empty voice">
            No accounts yet. Nothing&rsquo;s wrong — ask me to add one and it starts counting toward net worth
            from that moment.
          </p>
        ) : (
          <div className="money-groups">
            {groups.map((g) => {
              const st = STATUS_COPY[g.status];
              return (
                <article key={g.label} className="money-group">
                  <div className="money-group-head">
                    <div className="money-group-id">
                      <h3 className="money-group-name">{g.label}</h3>
                      {g.status !== 'active' && <span className={`money-chip ${st.tone}`}>{st.label}</span>}
                    </div>
                  </div>

                  <ul className="money-acct-list">
                    {g.accounts.map((a) => (
                      <AccountRow
                        key={a.id}
                        account={a}
                        busy={Boolean(acctBusy[a.id])}
                        onToggleHidden={() => toggleHidden(a)}
                      />
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <TransactionsTable transactions={transactions} />
      <CategoryPanel categories={categories} />
      <HoldingsPanel holdings={holdings} />
    </section>
  );
}
